import { get } from 'lodash';
import React from 'react';
import { EnumFieldType } from 'src/components/FieldModal/config';
import Tooltip, {
  NoWrapContainer,
} from 'src/components/GlobalTooltip/GlobalTooltip';
import TagGroup from 'src/components/TagGroup/TagGroup';
import Actions from 'src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelCheckout/Components/TableActions';
import { isUserValid, storeResourceNameGenerator } from 'src/tools/getUserInfo';
import { EnumTablePosition } from 'src/types/common/common';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from 'src/types/common/resources';
import { handleOnGetStoreTimezoneUTC } from '@/src/tools/tools';
import { AreaCodeText } from 'src/components/AreaCodeInfo';
import { maskPhone } from '@/src/tools/mask';
import { fieldValueGenerator } from '../tools';

const colWidth = {
  [EnumFieldType.CustomerID]: {
    width: { fixedWidth: 240 },
    i18nKey: 'customerList.customerDetail_config.customerID',
  },
  [EnumFieldType.RegisteredDate]: {
    width: { minWidth: 200 },
    i18nKey: 'customerList.layout.registeredDate',
  },
  [EnumFieldType.RegisteredSalesChannel]: {
    width: { minWidth: 280 },
    i18nKey: 'customerList.layout.registeredSalesChannel',
  },
  [EnumFieldType.AccountCreatedDate]: {
    width: { minWidth: 200 },
    i18nKey: 'customerList.layout.accountCreatedDate',
  },
  [EnumFieldType.TotalOrderAmount]: {
    width: { minWidth: 200 },
    i18nKey: 'customerList.layout.totalOrderAmount',
  },
  [EnumFieldType.TotalOrderNumber]: {
    width: { minWidth: 200 },
    i18nKey: 'customerList.layout.totalOrderNumber',
  },
  [EnumFieldType.AverageOrderAmount]: {
    width: { minWidth: 216 },
    i18nKey: 'customerList.layout.averageOrderAmount',
  },
  [EnumFieldType.LastOrderAmount]: {
    width: { minWidth: 200 },
    i18nKey: 'customerList.layout.lastOrderAmount',
  },
  [EnumFieldType.Email]: {
    width: { minWidth: 200 },
    i18nKey: 'customerList.layout.email',
  },
  [EnumFieldType.MobileNumber]: {
    width: { minWidth: 200 },
    i18nKey: 'customerList.layout.mobileNumber',
  },
  [EnumFieldType.AreaCodeMobileNumber]: {
    width: { minWidth: 200 },
    i18nKey: 'customerList.layout.mobileNumber',
  },
  [EnumFieldType.FirstName]: {
    width: { minWidth: 140 },
    i18nKey: 'customerList.customerDetail_config.firstName',
  },
  [EnumFieldType.LastName]: {
    width: { minWidth: 140 },
    i18nKey: 'customerList.customerDetail_config.lastName',
  },
  [EnumFieldType.FullName]: {
    width: { minWidth: 200 },
    i18nKey: 'customerList.layout.fullName',
  },
  [EnumFieldType.CrossBorderName]: {
    width: { minWidth: 200 },
    i18nKey: 'customerList.layout.cross-borderName',
  },
  [EnumFieldType.DateOfBirth]: {
    width: { minWidth: 140 },
    i18nKey: 'customerList.layout.dateofBirth',
  },
  [EnumFieldType.Gender]: {
    width: { minWidth: 140 },
    i18nKey: 'customerList.layout.gender',
  },
  [EnumFieldType.Title]: {
    width: { minWidth: 80 },
    i18nKey: 'customerList.layout.title',
  },
  [EnumFieldType.Language]: {
    width: { minWidth: 280 },
    i18nKey: 'customerList.layout.language',
  },
  [EnumFieldType.CustomerRemarks]: {
    width: { minWidth: 200 },
    i18nKey: 'customerList.layout.customerRemarks',
  },
  [EnumFieldType.CustomerManualTags]: {
    width: { minWidth: 280 },
    i18nKey: 'customerList.layout.customerManualTags',
  },
  [EnumFieldType.CustomerAutoTags]: {
    width: { minWidth: 280 },
    i18nKey: 'customerList.layout.customerAutoTags',
  },
  [EnumFieldType.ConsentAttribute]: {
    width: { minWidth: 280 },
    i18nKey: 'customerList.layout.consentAttribute',
  },
  [EnumFieldType.SubscriptionStatus]: {
    width: { minWidth: 160 },
    i18nKey: 'customerList.layout.subscriptionStatus',
  },
};

const tagDict = [
  EnumFieldType.CustomerManualTags,
  EnumFieldType.CustomerAutoTags,
];
const getFileType = (name: string) => {
  switch (name) {
    case 'Cross-border Name':
      return EnumFieldType.CrossBorderName;
    case 'Language':
      return EnumFieldType.Language;
    default:
      return name;
  }
  if (name === '') {
  }
};
const useCols = (fields, onView, { getI18n }) => {
  console.log('fields: ', fields);
  const canView = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Customers,
        subCategory: EnumSubCategory.CustomerDetailsPage,
        operation: EnumOperation.access_page_and_view_customer_details_page,
      }),
    ],
  });
  const a = fields.map((i) => {
    const { field, defaultShow, mustShow, sortable } = i || {};

    const { name, uuid, fieldType: _fieldType } = field || {};

    const fieldType = _fieldType ?? getFileType(name);
    const isMobile = _fieldType === EnumFieldType.AreaCodeMobileNumber;

    const width = (colWidth[fieldType] || {}).width || { minWidth: 200 };
    const timezoneInUTC = handleOnGetStoreTimezoneUTC();
    const i18nText = colWidth[fieldType]
      ? fieldType === 'RegisteredDate'
        ? `${getI18n(colWidth[fieldType].i18nKey)} (UTC ${timezoneInUTC})`
        : getI18n(colWidth[fieldType].i18nKey)
      : name;

    const _isTag = tagDict.includes(fieldType);
    return {
      ...width,
      key: fieldType || uuid,
      default: defaultShow,
      sorter: sortable,
      sortOrder: '',
      optional: !mustShow,
      textTitle: i18nText,
      titleGenerate: () => (
        <NoWrapContainer style={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={i18nText}>{i18nText}</Tooltip>
        </NoWrapContainer>
      ),

      // title: name,
      tags: _isTag,
      //  {..._isTag?{}:{childGenerate:}}
      ...(_isTag
        ? {
            tagsGenerator: (
              characterLength,
              width,
              marginPaddingWidth,
              containPaddingWidth,
              tags,
              record,
            ) => {
              const customerInfoFields =
                get(record, 'customerInfoFields') ||
                get(record, 'fields') ||
                [];
              const field = customerInfoFields.find((i) => {
                const uid = get(i, 'field.uuid');
                return uid === uuid;
              });

              const value = get(field, 'value') || get(field, 'values') || [];
              const _tags = value.map((i) => {
                return { name: i };
              });
              return (
                <TagGroup
                  characterLength={characterLength}
                  containerWidth={width}
                  marginPaddingWidth={marginPaddingWidth}
                  containPaddingWidth={containPaddingWidth}
                  tags={_tags}
                />
              );
            },
          }
        : {
            childGenerate: (_, record) => {
              const customerInfoFields =
                get(record, 'customerInfoFields') ||
                get(record, 'fields') ||
                [];

              const field = customerInfoFields.find((i) => {
                const uid = get(i, 'field.uuid');
                return uid === uuid;
              });
              const _field = get(field, 'field') || {};
              const _fieldName = _field?.name || '';

              const value = get(field, 'value') || get(field, 'values') || [];

              const display = value.length > 0 ? value.join(' ') : '-';

              const mobileGenerator = () => {
                const aUuid = get(value, 0);
                const mobile = get(value, 1);

                if (!aUuid) {
                  return null;
                }
                return (
                  <div style={{ display: 'flex' }}>
                    <AreaCodeText areaCodeUuid={aUuid} /> &nbsp;
                    <Tooltip title={mobile}>{maskPhone(mobile)}</Tooltip>
                  </div>
                );
              };
              return (
                <NoWrapContainer>
                  {isMobile ? (
                    mobileGenerator()
                  ) : (
                    <Tooltip title={display}>
                      {fieldValueGenerator(
                        _fieldName,
                        customerInfoFields,
                        display,
                      )}
                    </Tooltip>
                  )}
                </NoWrapContainer>
              );
            },
          }),

      // childGenerate: (_, record) => {
      //   const customerInfoFields =
      //     get(record, 'customerInfoFields') || get(record, 'fields') || [];

      //   const field = customerInfoFields.find((i) => {
      //     const uid = get(i, 'field.uuid');
      //     return uid === uuid;
      //   });
      //   console.log('field', field);

      //   const value = get(field, 'value') || get(field, 'values') || [];

      //   const display = value.length > 0 ? value.join(' ') : '-';

      //   return (
      //     <NoWrapContainer>
      //       <Tooltip title={display}>{display}</Tooltip>
      //     </NoWrapContainer>
      //   );
      // },
    };
  });
  const re = [
    ...a,
    ...(canView
      ? [
          {
            titleGenerate: () =>
              getI18n('customerTag.customerTag_List.actions'),
            position: EnumTablePosition.mid,
            key: 'Actions',
            default: true,
            childGenerate: (url, record, index) => {
              const { uuid } = record;
              return (
                <Actions
                  editText={getI18n('general.view')}
                  onEdit={() => {
                    if (onView) {
                      onView(uuid);
                    }
                    //  remove(index);
                  }}
                />
              );
            },
            fixedWidth: 190,
          },
        ]
      : []),
  ];
  //console.log('col', a);
  return re;
};
export { useCols };
